import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { Network, Code2, Rocket, Sparkles, Users, Grid3x3 } from 'lucide-react';
export function SolutionSlide() {
  const benefits = [{
    icon: <Network className="w-6 h-6" data-id="element-205" />,
    title: 'Open Ecosystem',
    desc: 'Seamlessly integrate with your existing CPMS and charger infrastructure. No vendor lock-in, complete flexibility.',
    color: 'blue',
    angle: -90 // Top
  }, {
    icon: <Code2 className="w-6 h-6" data-id="element-206" />,
    title: 'API-First Architecture',
    desc: 'Build custom solutions with our comprehensive API. Infinite possibilities for automation and integration.',
    color: 'purple',
    angle: -30 // Top-right
  }, {
    icon: <Rocket className="w-6 h-6" data-id="element-207" />,
    title: 'Launch in Days',
    desc: 'Get started immediately with our ready-made white-label application and pre-built components.',
    color: 'green',
    angle: 30 // Bottom-right
  }, {
    icon: <Sparkles className="w-6 h-6" data-id="element-208" />,
    title: 'Smart Charging',
    desc: 'Deliver exceptional EV driver experiences with market-specific optimization features.',
    color: 'yellow',
    angle: 90 // Bottom
  }, {
    icon: <Users className="w-6 h-6" data-id="element-209" />,
    title: 'Customer Ownership',
    desc: 'Maintain direct customer relationships. You remain the primary interface for all services.',
    color: 'red',
    angle: 150 // Bottom-left
  }, {
    icon: <Grid3x3 className="w-6 h-6" data-id="element-210" />,
    title: 'Universal Compatibility',
    desc: 'One unified interface works across all charger brands. Simplify operations dramatically.',
    color: 'cyan',
    angle: 210 // Top-left
  }];
  const colorMap = {
    blue: 'border-blue-500/50 bg-blue-500/10 text-blue-400',
    purple: 'border-purple-500/50 bg-purple-500/10 text-purple-400',
    green: 'border-green-500/50 bg-green-500/10 text-green-400',
    yellow: 'border-yellow-500/50 bg-yellow-500/10 text-yellow-400',
    red: 'border-red-500/50 bg-red-500/10 text-red-400',
    cyan: 'border-cyan-500/50 bg-cyan-500/10 text-cyan-400'
  };
  const centerX = 300;
  const centerY = 300;
  const radius = 260; // Increased from 240 to prevent overlap
  return <div className="h-full w-full flex items-center px-8 md:px-16 bg-slate-950 gap-12" data-id="element-211">
      {/* Left side - Text content */}
      <motion.div initial={{
      opacity: 0,
      x: -50
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      duration: 0.8
    }} className="flex-1 max-w-xl z-10" data-id="element-212">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-900/30 border border-blue-800 text-blue-400 text-sm font-medium mb-6" data-id="element-213">
          <Network className="w-4 h-4" data-id="element-214" />
          <span data-id="element-215">Integrated Platform</span>
        </div>

        <h2 className="text-5xl font-bold text-white mb-6 leading-tight" data-id="element-216">
          Our Smart Charging <br data-id="element-217" />
          <span className="text-blue-500" data-id="element-218">Ecosystem</span>
        </h2>

        <p className="text-xl text-slate-400 leading-relaxed" data-id="element-219">
          A complete ecosystem designed to make your charging operations smarter
          and more efficient, while significantly reducing operational risk and
          vendor dependency.
        </p>
      </motion.div>

      {/* Right side - Ecosystem visualization */}
      <div className="flex-1 relative min-h-[650px] flex items-center justify-center" data-id="element-220">
        <div className="relative w-[650px] h-[650px]" data-id="element-221">
          {/* Central Core Hub - positioned at exact center point */}
          <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} className="absolute z-20" style={{
          left: `${centerX}px`,
          top: `${centerY}px`
        }} data-id="element-222">
            <div className="w-28 h-28 -ml-14 -mt-14 bg-slate-900 border-4 border-blue-500 rounded-full flex items-center justify-center shadow-[0_0_60px_rgba(59,130,246,0.4)]" data-id="element-223">
              <span className="text-white font-bold text-lg tracking-wider" data-id="element-224">
                CORE
              </span>
            </div>

            {/* Pulsing rings */}
            <motion.div className="absolute inset-0 border-2 border-blue-500/30 rounded-full" animate={{
            scale: [1, 1.4, 1],
            opacity: [0.5, 0, 0.5]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-225" />
          </motion.div>

          {/* Benefit Cards in circular arrangement */}
          {benefits.map((benefit, idx) => {
          const angleRad = benefit.angle * Math.PI / 180;
          const x = Math.cos(angleRad) * radius;
          const y = Math.sin(angleRad) * radius;
          // Card dimensions - reduced width to prevent overlap
          const cardWidth = 200; // Reduced from 224
          // Calculate position (center of card should be at x,y from center)
          const left = centerX + x - cardWidth / 2;
          const top = centerY + y - 70; // Approximate half height
          return <Fragment key={idx} data-id="element-226">
                {/* Connection line to core */}
                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 1,
              delay: 0.5 + idx * 0.1
            }} className="absolute pointer-events-none" style={{
              left: `${centerX}px`,
              top: `${centerY}px`,
              width: '2px',
              height: `${radius}px`,
              background: 'linear-gradient(to bottom, rgba(59, 130, 246, 0.3), transparent)',
              transformOrigin: 'top center',
              transform: `rotate(${benefit.angle}deg)`
            }} data-id="element-227" />

                {/* Benefit Card */}
                <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              delay: 0.5 + idx * 0.1
            }} className="absolute" style={{
              left: `${left}px`,
              top: `${top}px`,
              width: `${cardWidth}px`
            }} data-id="element-228">
                  <motion.div animate={{
                y: [0, -6, 0]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: idx * 0.5
              }} className={`p-4 rounded-xl border-2 backdrop-blur-sm ${colorMap[benefit.color as keyof typeof colorMap]} shadow-xl`} data-id="element-229">
                    <div className="flex items-start gap-3 mb-2" data-id="element-230">
                      <div className="p-2 rounded-lg bg-slate-950/50 border border-slate-700 flex-shrink-0" data-id="element-231">
                        {benefit.icon}
                      </div>
                      <h3 className="text-sm font-bold text-white leading-tight" data-id="element-232">
                        {benefit.title}
                      </h3>
                    </div>
                    <p className="text-xs text-slate-300 leading-relaxed" data-id="element-233">
                      {benefit.desc}
                    </p>
                  </motion.div>
                </motion.div>
              </Fragment>;
        })}
        </div>
      </div>
    </div>;
}